import { ThreeToolsInstance, usePartDefinition } from "@project/emerge";
import { markProductSceneChanged } from "@project/emerge/src/new/sceneActivity";
import { Sides } from "@project/emerge-types";
import { useLayoutEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { MiterType } from "../enum/MiterType";
import { profileGeometryCache } from "../geometry/FinishedGeometryCache";
import { profileGeometryKey } from "../geometry/profileGeometryKey";
import {
  disposeExtrudedAluminumMaterial,
  getProfileAluminumMaterial,
} from "../materials/AluminumProfileMaterial";
import type { ProfileDefinition } from "../parts/ProfileDefinition";
import { ProfiledContourGeometry } from "../parts/ProfiledContourGeometry";
import { outerFaceGroovePoints } from "../parts/profileGroovePoints";
import { roundProfileCorners } from "../parts/roundProfileCorners";
import { RectPropertiesBySide } from "../structure/RectProperties";
import { PartEdges } from "./PartEdges";
import { useLatestCommittedResource } from "./useLatestCommittedResource";

type ProfileRendererProps = Readonly<{
  id: string;
}>;

type VectorAxis = "x" | "y" | "z";

function asVectorAxis(axis: string | undefined): VectorAxis | undefined {
  return axis === "x" || axis === "y" || axis === "z" ? axis : undefined;
}

export const ProfileRenderer = ({ id }: ProfileRendererProps) => {
  // Subscribe to properties needed for rendering
  // Position is NOT subscribed - it's handled by PartRendererComponent parent
  // This allows position changes without geometry regeneration
  const partDef = usePartDefinition<ProfileDefinition>({
    id,
    select: ["partSelection", "points", "cutReference", "miter", "inverse"],
  });

  const meshRef = useRef<THREE.Mesh | null>(null);

  const cutReference = partDef?.cutReference;
  const rectEntry =
    cutReference == null ? undefined : RectPropertiesBySide[cutReference];
  const vertical = asVectorAxis(rectEntry?.vertical);
  const horizontal = asVectorAxis(rectEntry?.horizontal);
  const normal = asVectorAxis(rectEntry?.normal);
  const ccw =
    cutReference != null &&
    ![Sides.TOP, Sides.BACK, Sides.RIGHT].includes(cutReference);

  // Build the 2D profile shape.
  const profile2D = useMemo(() => {
    if (
      !partDef?.partSelection ||
      vertical === undefined ||
      horizontal === undefined ||
      normal === undefined
    ) {
      return [] as Array<[number, number]>;
    }
    const ps = partDef.partSelection;
    const { inverse } = partDef;
    const gapWidth = ps.gapWidth;
    const height = ps.height;
    const s = ps.strength;
    const t = ps.extra - 2 * s;
    let result: Array<[number, number]>;

    if (ps.description.slice(0, 3) !== "M/F") {
      const a = s + (inverse !== ps.inverted ? t : 0);
      const b = s + (inverse !== ps.inverted ? 0 : t);
      const x = (n: number): number => {
        switch (n) {
          case 0:
            return -gapWidth - s;
          case 5:
            return s;
          default:
            return ((n - 1) / 3 - 1) * gapWidth;
        }
      };
      result = [
        [x(1), 0],
        [x(4), 0],
        [x(4), height],
        [x(5), height],
        [x(5), -a],
        [x(4), -a],
        [x(3), -b],
        [x(2), -a],
        [x(1), -b],
        [x(0), -b],
        [x(0), height],
        [x(1), height],
      ];
    } else {
      const a = s + (inverse !== ps.inverted ? t : 0);
      const b = s + (inverse !== ps.inverted ? 0 : t);
      const x = (n: number): number => {
        switch (n) {
          case 0:
            return -gapWidth - s;
          case 4:
            return s;
          default:
            return ((n - 1) / 2 - 1) * gapWidth;
        }
      };
      result = [
        [x(1), 0],
        [x(3), 0],
        [x(3), height],
        [x(4), height],
        [x(4), -a],
        [x(3), -a],
        [x(2), -b],
        [x(1), -a],
        [x(0), -b],
        [x(0), height],
        [x(1), height],
      ];
    }
    return result.map<[number, number]>((p) => [
      p[0],
      !ccw ? p[1] + ps.extra / 2 : -p[1] - ps.extra / 2,
    ]);
  }, [partDef, vertical, horizontal, normal, ccw]);

  const profileShape = useMemo(() => {
    if (profile2D.length < 3) {
      return null;
    }
    // Real extrusions carry a die radius on every corner; without one the
    // section cannot catch the travelling highlight that reads as aluminium.
    return ThreeToolsInstance.pointsToShape(roundProfileCorners(profile2D));
  }, [profile2D]);

  // Build a linear extrusion path with straight segments, centered at middle of middle segment.
  // This centering matches the buildstep's position calculation.
  const extrudeInfo = useMemo(() => {
    const points = partDef?.points;
    if (
      !points?.length ||
      vertical === undefined ||
      horizontal === undefined ||
      normal === undefined
    ) {
      return { path: new THREE.CurvePath<THREE.Vector3>(), steps: 0 };
    }
    const pts = !ccw ? [...points].reverse() : points;
    // Transform to geometry space
    const geoPoints = pts.map(
      (p: THREE.Vector3) =>
        new THREE.Vector3(p[horizontal], p[vertical], p[normal]),
    );

    // Calculate center as middle of middle segment (same logic as buildstep)
    const midSegmentIdx = Math.floor((geoPoints.length - 1) / 2);
    const center = new THREE.Vector3();
    if (geoPoints.length >= 2) {
      const midA = geoPoints[midSegmentIdx];
      const midB = geoPoints[midSegmentIdx + 1];
      if (midA && midB) {
        center.copy(midA).add(midB).multiplyScalar(0.5);
      }
    } else if (geoPoints.length === 1) {
      const only = geoPoints[0];
      if (only) {
        center.copy(only);
      }
    }

    // Center the points
    const centeredPoints = geoPoints.map((p) => p.clone().sub(center));

    const path = new THREE.CurvePath<THREE.Vector3>();
    for (let i = 0; i < centeredPoints.length - 1; i++) {
      const a = centeredPoints[i];
      const b = centeredPoints[i + 1];
      if (a && b) {
        path.add(new THREE.LineCurve3(a, b));
      }
    }
    // Set steps to exactly the number of segments (or more if desired).
    const steps = Math.max(1, centeredPoints.length - 1);
    return { path, steps };
  }, [partDef?.points, ccw, horizontal, vertical, normal]);

  const contour = useMemo(
    () =>
      extrudeInfo.path
        .getPoints(extrudeInfo.steps)
        .map((p) => new THREE.Vector2(p.x, p.y)),
    [extrudeInfo],
  );
  const finalMiterType = partDef?.miter || MiterType.NONE;
  const geometryKey =
    profileShape && extrudeInfo.steps >= 1 && partDef?.points?.length
      ? profileGeometryKey(profile2D, contour, finalMiterType, ccw)
      : null;
  // The material is leased with the geometry rather than memoised beside it.
  // It is sized from the section — the die lines are a physical pattern, so it
  // has to know how many millimetres the normalised U spans, and the grooves
  // sit where the geometry puts the outer faces — but it also owns two cloned
  // textures, and `useMemo`
  // would strand a set of those on every render that is abandoned before it
  // commits (a StrictMode double render, a suspended sibling). Leasing it here
  // rather than through a second `useLatestCommittedResource` keeps geometry and
  // material arriving in the same commit, so the mesh is never mounted with one
  // and not the other. The bake behind the material is shared by every profile.
  const part = useLatestCommittedResource(geometryKey, () => {
    if (!profileShape || geometryKey === null) {
      throw new Error("Missing profile geometry inputs");
    }
    const lease = profileGeometryCache.acquire(
      geometryKey,
      () =>
        new ProfiledContourGeometry(profileShape, contour, finalMiterType, ccw),
    );
    let material: THREE.MeshStandardMaterial;
    try {
      // Two grooves down each outer face, as on the Kantenschutz: named as
      // points on the section, mapped to texture U by the geometry.
      const grooveU = outerFaceGroovePoints(profile2D).map((point) =>
        lease.value.sectionU(point),
      );
      material = getProfileAluminumMaterial(lease.value.sectionWidth, grooveU);
    } catch (error) {
      lease.release();
      throw error;
    }
    return {
      value: { geometry: lease.value, material },
      release: () => {
        lease.release();
        disposeExtrudedAluminumMaterial(material);
      },
    };
  });
  const geometry = part?.geometry ?? null;
  useLayoutEffect(() => {
    if (geometry && meshRef.current) markProductSceneChanged(meshRef.current);
  }, [geometry]);

  if (!partDef?.points?.length || !part) {
    return null;
  }

  // Return the rendering - position is applied by PartRendererComponent parent
  // Geometry is centered at middle of middle segment, matching buildstep's position
  return (
    <group>
      {/* Props, not `<primitive attach>`: R3F never disposes a prop value, and
          the lease owns both. See `useLatestCommittedResource`. */}
      <mesh ref={meshRef} geometry={part.geometry} material={part.material}>
        <PartEdges />
      </mesh>
    </group>
  );
};
